type CacheEntry<T> = {
  expiresAt: number;
  hasValue: boolean;
  promise?: Promise<T>;
  value?: T;
};

type CacheOptions = {
  force?: boolean;
  ttlMs?: number;
};

const requestCache = new Map<string, CacheEntry<unknown>>();

export async function readCachedResource<T>(
  key: string,
  loader: () => Promise<T>,
  options: CacheOptions = {},
): Promise<T> {
  const ttlMs = Math.max(0, options.ttlMs ?? 60_000);
  const now = Date.now();
  const existing = requestCache.get(key) as CacheEntry<T> | undefined;

  if (!options.force) {
    if (existing?.hasValue && existing.expiresAt > now) {
      return existing.value as T;
    }
    if (existing?.promise) {
      return existing.promise;
    }
  }

  const promise = loader()
    .then((value) => {
      requestCache.set(key, {
        expiresAt: Date.now() + ttlMs,
        hasValue: true,
        value,
      });
      return value;
    })
    .catch((error) => {
      const current = requestCache.get(key) as CacheEntry<T> | undefined;
      if (current?.promise === promise) {
        if (current.hasValue && current.expiresAt > Date.now()) {
          requestCache.set(key, {
            expiresAt: current.expiresAt,
            hasValue: true,
            value: current.value,
          });
        } else {
          requestCache.delete(key);
        }
      }
      throw error;
    });

  requestCache.set(key, {
    expiresAt: existing?.expiresAt ?? 0,
    hasValue: existing?.hasValue === true,
    promise,
    value: existing?.value,
  });

  return promise;
}

export function invalidateCachedResource(key: string): void {
  requestCache.delete(key);
}

export function invalidateCachedResourcesByPrefix(prefix: string): void {
  for (const key of requestCache.keys()) {
    if (key.startsWith(prefix)) {
      requestCache.delete(key);
    }
  }
}
